import { ReactNode, useMemo, useState } from 'react';
import * as R from 'remeda';
import { Stack, Typography } from '@rossum/ui/material';
import { useIntl } from '../../ComponentsContext';
import { getFilteredOptions } from '../helpers';
import { highlightSearchedValue } from '../highlightSearchedValue';
import { areOptionsGrouped, OptionGroup } from '../types';
import {
  GroupedOptionsList,
  SelectOption,
  SelectOptionsList,
} from './SelectComponents';

export const useFilterSelectProps = ({
  options,
  currentValues,
  getOptionLabel,
  searchValue,
  isSingleSelect,
}: {
  currentValues: string[];
  options: string[] | OptionGroup[];
  getOptionLabel: (v: string) => string;
  searchValue: string;
  isSingleSelect: boolean;
}) => {
  const intl = useIntl();
  const [selectedValuesMap, setSelectedValuesMap] = useState(
    R.mapToObj(
      currentValues.map(c => c.toString()),
      v => [v, true]
    )
  );

  const normalizedOptions = useMemo(() => {
    return searchValue
      ? getFilteredOptions(options, searchValue, getOptionLabel)
      : options;
  }, [getOptionLabel, options, searchValue]);

  const ListComponent = useMemo(() => {
    const onSelect = (value: string) => {
      if (isSingleSelect) {
        setSelectedValuesMap({ [value]: true });
      } else setSelectedValuesMap(prev => ({ ...prev, [value]: !prev[value] }));
    };

    if (normalizedOptions.length === 0)
      return (
        <Stack
          sx={{ justifyContent: 'center', alignItems: 'center', width: 1 }}
        >
          {searchValue ? (
            <Typography variant="body2" color="text.secondary">
              {intl.formatMessage(
                {
                  id: 'components.filterPanel.filterMultiSelect.empty.search',
                },
                {
                  boldText: (chunks: ReactNode[]) => (
                    <Typography
                      variant="inherit"
                      component="span"
                      sx={{ fontWeight: 'bold' }}
                    >
                      {chunks}
                    </Typography>
                  ),
                  value: searchValue,
                }
              )}
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {intl.formatMessage({
                id: 'components.filterPanel.filterMultiSelect.empty.noOptions',
              })}
            </Typography>
          )}
        </Stack>
      );
    return areOptionsGrouped(normalizedOptions) ? (
      <GroupedOptionsList
        searchValue={searchValue}
        groupedOptions={normalizedOptions}
        selectedValues={selectedValuesMap}
        onClickOnAll={
          isSingleSelect
            ? undefined
            : selectedMap =>
                setSelectedValuesMap(prev => ({ ...prev, ...selectedMap }))
        }
        renderOptions={optionsOfGroup => {
          return optionsOfGroup.map(option => (
            <SelectOption
              key={option}
              checked={!!selectedValuesMap[option]}
              label={
                searchValue
                  ? highlightSearchedValue(getOptionLabel(option), searchValue)
                  : getOptionLabel(option)
              }
              handleOnClick={() => onSelect(option)}
              dataCy={option.toString()}
              isSingleSelect={isSingleSelect}
            />
          ));
        }}
      />
    ) : (
      <SelectOptionsList>
        {normalizedOptions.map(option => (
          <SelectOption
            key={option}
            checked={!!selectedValuesMap[option]}
            label={
              searchValue
                ? highlightSearchedValue(getOptionLabel(option), searchValue)
                : getOptionLabel(option)
            }
            handleOnClick={() => onSelect(option)}
            isSingleSelect={isSingleSelect}
            dataCy={option.toString()}
          />
        ))}
      </SelectOptionsList>
    );
  }, [
    normalizedOptions,
    searchValue,
    intl,
    selectedValuesMap,
    isSingleSelect,
    getOptionLabel,
  ]);

  const selectedValues = useMemo(
    () =>
      R.entries(selectedValuesMap).flatMap(([value, selected]) =>
        selected ? value : []
      ),
    [selectedValuesMap]
  );

  return { ListComponent, selectedValues, searchValue };
};
